<template>
    <nav class="app-sidebar" aria-label="Primary navigation">
        <!-- Primary work - deliberately unlabeled. "Dashboard" is a natural entry point and a
             "Home" header over it would read as redundant. -->
        <div v-if="visibleNavigationLinks.length" class="app-sidebar__section">
            <ul class="app-sidebar__items">
                <li v-for="item in visibleNavigationLinks" :key="item.viewkey">
                    <a
                        :href="item.url"
                        class="app-sidebar__link"
                        :class="{ 'app-sidebar__link--active': isActive(item.viewkey) }"
                    >
                        <q-icon :name="item.icon" size="18px" class="app-sidebar__link-icon" />
                        <span class="app-sidebar__link-label">{{ item.text }}</span>
                    </a>
                </li>
            </ul>
        </div>

        <!-- Organization (array is still called settingsLinks for minimal file churn) -->
        <div v-if="visibleSettingsLinks.length" class="app-sidebar__section">
            <div class="app-sidebar__section-label">Organization</div>
            <ul class="app-sidebar__items">
                <li v-for="item in visibleSettingsLinks" :key="item.viewkey">
                    <a
                        :href="item.url"
                        class="app-sidebar__link"
                        :class="{ 'app-sidebar__link--active': isActive(item.viewkey) }"
                    >
                        <q-icon :name="item.icon" size="18px" class="app-sidebar__link-icon" />
                        <span class="app-sidebar__link-label">{{ item.text }}</span>
                    </a>
                </li>
            </ul>
        </div>

        <!-- Billing & Payments (array is still called enterpriseLinks for minimal file churn) -->
        <div v-if="visibleEnterpriseLinks.length" class="app-sidebar__section">
            <div class="app-sidebar__section-label">Billing &amp; Payments</div>
            <ul class="app-sidebar__items">
                <li v-for="item in visibleEnterpriseLinks" :key="item.viewkey">
                    <a
                        :href="item.url"
                        class="app-sidebar__link"
                        :class="{ 'app-sidebar__link--active': isActive(item.viewkey) }"
                    >
                        <q-icon :name="item.icon" size="18px" class="app-sidebar__link-icon" />
                        <span class="app-sidebar__link-label">{{ item.text }}</span>
                    </a>
                </li>
            </ul>
        </div>

        <!-- Developers -->
        <div v-if="visibleDeveloperLinks.length" class="app-sidebar__section">
            <div class="app-sidebar__section-label">Developers</div>
            <ul class="app-sidebar__items">
                <li v-for="item in visibleDeveloperLinks" :key="item.viewkey">
                    <a
                        :href="item.url"
                        class="app-sidebar__link"
                        :class="{ 'app-sidebar__link--active': isActive(item.viewkey) }"
                    >
                        <q-icon :name="item.icon" size="18px" class="app-sidebar__link-icon" />
                        <span class="app-sidebar__link-label">{{ item.text }}</span>
                    </a>
                </li>
            </ul>
        </div>

        <!-- Single entry to the canonical docs mini-site. Hairline rule above visually
             separates this from the functional nav - the docs mini-site is a different kind
             of content (read vs. manage) and lives as its own page at /Docs/IntegrationGuides. -->
        <div
            v-if="visibleDocumentationLinks.length"
            class="app-sidebar__section app-sidebar__section--docs"
        >
            <ul class="app-sidebar__items">
                <li v-for="item in visibleDocumentationLinks" :key="item.viewkey">
                    <a
                        :href="item.url"
                        class="app-sidebar__link"
                        :class="{ 'app-sidebar__link--active': isActive(item.viewkey) }"
                    >
                        <q-icon :name="item.icon" size="18px" class="app-sidebar__link-icon" />
                        <span class="app-sidebar__link-label">{{ item.text }}</span>
                    </a>
                </li>
            </ul>
        </div>
    </nav>
</template>

<script>
import { useAsideStore } from '@stores/aside.js';

/**
 * AppSidebar - Dashboard primary navigation.
 *
 * Visual language matches src/components/guides/shared/GuideSidebar.vue for site-wide
 * consistency: small uppercase section labels, 18-px icons, 13.5-px item text, indigo active
 * highlight. Both sidebars consume the same `guideTopics.js` for nested docs navigation.
 *
 * Entries with a `children` array render as an expandable section with a chevron. When a
 * child carries a `group` label, a small uppercase sub-header is inserted whenever the group
 * changes between consecutive children, producing the same Getting Started / Core Resources
 * / Authoring / Integration grouping seen in the public-mode GuideSidebar.
 *
 * CSS is intentionally unscoped so Quasar's default q-drawer/q-icon styles cannot override
 * ours via the cascade. Selectors are namespaced under .app-sidebar to avoid collisions.
 */
export default {
    name: 'AppSidebar',

    setup() {
        const store = useAsideStore();
        return { store };
    },

    data() {
        // Local open/closed state per expandable item (keyed by viewkey). Seeded from the
        // "this child is active" check so expansions auto-open when you land on a child page,
        // then stay at whatever state the user toggled.
        return {
            expansionState: {}
        };
    },

    computed: {
        effectivePermissions() {
            const permissions = window._USER_PERMISSIONS || {};
            const pageConfig = window._pageConfig || {};

            const isTenantAdmin = permissions.isTenantAdmin === true ||
                                  permissions.isTenantAdmin === 'true' ||
                                  pageConfig.isTenantAdmin === true ||
                                  pageConfig.isTenantAdmin === 'true' ||
                                  window._IS_TENANT_ADMIN === true ||
                                  window._IS_TENANT_ADMIN === 'true';

            const isBillingAdmin = permissions.isBillingAdmin === true ||
                                   permissions.isBillingAdmin === 'true' ||
                                   window._IS_BILLING_ADMIN === true ||
                                   window._IS_BILLING_ADMIN === 'true';

            const canManageTeam = permissions.canManageTeam === true ||
                                  permissions.canManageTeam === 'true' ||
                                  isTenantAdmin;

            const canManageBilling = permissions.canManageBilling === true ||
                                     permissions.canManageBilling === 'true' ||
                                     isTenantAdmin ||
                                     isBillingAdmin;

            // Test Settings (sidebar) is visible to anyone with sandbox access:
            // Tenant Admin (can create + use the sandbox) OR Developer
            // (can only enter an existing sandbox). The controller-side
            // [Authorize(Roles="Tenant Admin,Developer")] is the authoritative
            // gate; this just hides the menu row from users who can't use it.
            const isDeveloper = permissions.isDeveloper === true ||
                                permissions.isDeveloper === 'true';
            const canAccessDeveloperSettings = isTenantAdmin || isDeveloper;

            const normalized = {};
            for (const [key, val] of Object.entries(permissions)) {
                normalized[key] = val === true || val === 'true';
            }

            return {
                ...normalized,
                canManageTeam,
                canManageBilling,
                isTenantAdmin,
                isDeveloper,
                canAccessDeveloperSettings
            };
        },

        visibleNavigationLinks() {
            return this.store.navigationLinks.filter(link => this.linkVisible(link));
        },

        visibleSettingsLinks() {
            return this.store.settingsLinks.filter(link => this.linkVisible(link));
        },

        visibleEnterpriseLinks() {
            return this.store.enterpriseLinks.filter(link => this.linkVisible(link));
        },

        visibleDeveloperLinks() {
            return (this.store.developerLinks || []).filter(link => this.linkVisible(link));
        },

        visibleDocumentationLinks() {
            return (this.store.documentationLinks || []).filter(link => this.linkVisible(link));
        }
    },

    methods: {
        linkVisible(link) {
            // Sidebar links with requiresFeature are always visible to tenant admins
            // so they can discover features and see upgrade prompts on the page itself.
            // The FeatureGate component on each page handles the upgrade prompt.

            // Sandbox-hidden rows (Organization: Team / Branding / Identity & SSO) drop
            // off the nav when the resolved tenant has IsSandbox = 1. The pages still
            // exist and a direct-URL hit loads against the sandbox's isolated state -
            // this is a UX gate, not a security boundary. Per the 2026-05-19 design
            // call: the sandbox is for API/integration testing, not real-world
            // team/branding/SSO setup.
            if (link.hiddenInSandbox && this.store.isSandbox) {
                return false;
            }

            // Tenant-admin-only rows (e.g., API Keys) are strictly gated: the role
            // check bypasses any permission-based role inheritance. This matches
            // the corresponding backend controller's [Authorize(Roles="Tenant Admin")].
            if (link.requiresTenantAdmin) {
                return this.effectivePermissions.isTenantAdmin === true;
            }
            if (!link.requiresPermission) return true;
            return this.effectivePermissions[link.requiresPermission] === true;
        },
        isActive(viewkey) {
            return this.store.isActive(viewkey);
        },
        // Parent rows (expansions) should look "active" either when the parent itself is the
        // current page or when any nested child is. Mirrors the convention most sidebars use.
        isActiveOrChildActive(item) {
            if (this.store.isActive(item.viewkey)) return true;
            if (!item.children) return false;
            return item.children.some(child => this.store.isActive(child.viewkey));
        },
        isExpanded(item) {
            // Explicit user toggle wins. Otherwise auto-open when any child is the active page.
            const explicit = this.expansionState[item.viewkey];
            if (typeof explicit === 'boolean') return explicit;
            return this.store.isGroupExpanded(item);
        },
        onExpansionClick(event, item) {
            // Plain left-click toggles in-place. Modifier-clicks let the href navigate so
            // middle-click / cmd-click / ctrl-click still open the link in a new tab.
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
            event.preventDefault();
            const current = this.isExpanded(item);
            this.expansionState = { ...this.expansionState, [item.viewkey]: !current };
        },
        shouldShowGroupLabel(children, idx) {
            const child = children[idx];
            if (!child.group) return false;
            if (idx === 0) return true;
            return children[idx - 1].group !== child.group;
        }
    },

    mounted() {
    }
};
</script>

<!-- Unscoped so the selectors win against Quasar's q-drawer / q-icon defaults. Selectors
     are all namespaced under .app-sidebar so they don't leak elsewhere. -->
<style>
/* Just padding and font inheritance - q-drawer__content already has `fit scroll`, so it
   handles independent scroll. Adding overflow-y here creates a nested scroll container that
   fights with the drawer's built-in scroll behavior. */
.app-sidebar {
    padding: 12px 8px;
    font-family: inherit;
}

/* Sections */
.app-sidebar .app-sidebar__section {
    margin-bottom: 22px;
}

/* Visual break above the docs/reference zone - signals the shift from "things I manage"
   in the nav above to "things I read" below. Hairline rule + extra breathing room. */
.app-sidebar .app-sidebar__section--docs {
    border-top: 1px solid #e2e8f0;
    margin-top: 10px;
    padding-top: 18px;
}

/* Banner treatment for the Knowledge Base section label. Makes the docs zone read as a
   distinct "chapter divider" in the sidebar rather than just another section header.
   Applied via the `--banner` modifier so default section labels elsewhere stay minimal. */
.app-sidebar .app-sidebar__section-label--banner {
    display: block;
    text-align: center;
    font-size: 12.5px;
    letter-spacing: 0.12em;
    color: #ffffff !important;
    background: #4F46E5;
    padding: 9px 12px;
    margin: 0 0 14px 0;
    border-radius: 0;
    box-shadow:
        0 2px 6px rgba(79, 70, 229, 0.35),
        0 1px 2px rgba(15, 23, 42, 0.08);
}

.app-sidebar .app-sidebar__section-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #64748b;
    padding: 0 12px;
    margin-bottom: 6px;
}

.app-sidebar .app-sidebar__items {
    list-style: none;
    padding: 0;
    margin: 0;
}

.app-sidebar .app-sidebar__items li {
    list-style: none;
}

/* Top-level links */
.app-sidebar .app-sidebar__link {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 7px 12px;
    border-radius: 6px;
    color: #334155;
    text-decoration: none;
    font-size: 13.5px;
    line-height: 1.3;
    transition: background 100ms ease-out, color 100ms ease-out;
    cursor: pointer;
    user-select: none;
}

.app-sidebar .app-sidebar__link:hover {
    background: #f1f5f9;
    color: #0f172a;
}

.app-sidebar .app-sidebar__link--active,
.app-sidebar .app-sidebar__link--active:hover {
    background: #EEF2FF;
    color: #3730A3;
    font-weight: 600;
}

/* Force our icon color even when Quasar applies its own. Targets .q-icon which is the
   class Quasar applies to <q-icon> elements regardless of how we nest them. */
.app-sidebar .app-sidebar__link-icon.q-icon,
.app-sidebar .app-sidebar__link-icon {
    color: #64748b !important;
    flex-shrink: 0;
}

.app-sidebar .app-sidebar__link--active .app-sidebar__link-icon.q-icon,
.app-sidebar .app-sidebar__link--active .app-sidebar__link-icon {
    color: #4F46E5 !important;
}

.app-sidebar .app-sidebar__link-label {
    flex: 1;
    min-width: 0;
}

.app-sidebar .app-sidebar__expansion-chevron.q-icon,
.app-sidebar .app-sidebar__expansion-chevron {
    color: #94a3b8 !important;
    flex-shrink: 0;
}

/* Child list for expansion items */
.app-sidebar .app-sidebar__children {
    list-style: none;
    padding: 2px 0 4px 0;
    margin: 0 0 4px 10px;
    border-left: 1px solid #e2e8f0;
}

.app-sidebar .app-sidebar__children li {
    list-style: none;
}

/* Inline sub-group label (Getting Started / Core Resources / ... inside Knowledge Base).
   Kept deliberately faint so it reads as soft taxonomy scaffolding rather than competing
   with the item labels for attention. */
.app-sidebar .app-sidebar__sublabel {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #cbd5e1;
    padding: 8px 12px 4px 14px;
}

/* Nested child link */
.app-sidebar .app-sidebar__sublink {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 5px 12px 5px 14px;
    border-radius: 6px;
    color: #475569;
    text-decoration: none;
    font-size: 12.5px;
    line-height: 1.3;
    transition: background 100ms ease-out, color 100ms ease-out;
}

.app-sidebar .app-sidebar__sublink:hover {
    background: #f1f5f9;
    color: #0f172a;
}

.app-sidebar .app-sidebar__sublink--active,
.app-sidebar .app-sidebar__sublink--active:hover {
    background: #EEF2FF;
    color: #3730A3;
    font-weight: 600;
}

.app-sidebar .app-sidebar__sublink-icon.q-icon,
.app-sidebar .app-sidebar__sublink-icon {
    color: #94a3b8 !important;
    flex-shrink: 0;
}

.app-sidebar .app-sidebar__sublink--active .app-sidebar__sublink-icon.q-icon,
.app-sidebar .app-sidebar__sublink--active .app-sidebar__sublink-icon {
    color: #4F46E5 !important;
}

.app-sidebar .app-sidebar__sublink-label {
    flex: 1;
    min-width: 0;
}
</style>
